import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiHelper } from '../lib/api';

const BASE = '/veh/vehicle-requests';

export const vehicleKeys = {
    all: ['vehicle-requests'],
    lists: (orgId) => [...vehicleKeys.all, 'list', orgId],
    list: (orgId, filters) => [...vehicleKeys.lists(orgId), filters],
    detail: (id) => [...vehicleKeys.all, 'detail', id],
    summary: (orgId) => [...vehicleKeys.all, 'summary', orgId],
    drivers: (orgId, search) => [...vehicleKeys.all, 'drivers', orgId, search],
};

// List endpoints may come back as a bare array or DRF-paginated.
const toList = (data) => (Array.isArray(data) ? data : (data?.results || []));

// Typed signatures ride as JSON; an uploaded image forces multipart.
export function buildTransitionPayload(data = {}) {
    const { signature, ...rest } = data;

    if (signature?.mode === 'upload' && signature.image) {
        const formData = new FormData();
        Object.entries(rest).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                formData.append(key, value);
            }
        });
        formData.append('signature_mode', 'upload');
        formData.append('signature_image', signature.image);
        return formData;
    }

    if (signature?.mode === 'typed' && signature.name?.trim()) {
        return { ...rest, signature: { mode: 'typed', name: signature.name.trim() } };
    }

    return rest;
}

// ─── READS ───────────────────────────────────────────────────────────────────

// Toast suppressed — a 403 here just means no vehicle access.
export function useVehicleRequests(orgId, filters = {}) {
    return useQuery({
        queryKey: vehicleKeys.list(orgId, filters),
        queryFn: () => apiHelper.getResource(`${BASE}/`, { orgId, params: filters, suppressToast: true }),
        enabled: !!orgId,
        select: toList,
        retry: false,
    });
}

export function useVehicleRequest(id, orgId) {
    return useQuery({
        queryKey: vehicleKeys.detail(id),
        queryFn: () => apiHelper.getResource(`${BASE}/${id}/`, { orgId }),
        enabled: !!orgId && !!id,
        retry: false,
    });
}

export function useVehicleRequestSummary(orgId, { enabled = true } = {}) {
    return useQuery({
        queryKey: vehicleKeys.summary(orgId),
        queryFn: () => apiHelper.getResource(`${BASE}/summary/`, { orgId, suppressToast: true }),
        enabled: !!orgId && enabled,
        staleTime: 30000,
        retry: false,
    });
}

// Active employees in the current org whose job title marks them as drivers.
export function useVehicleDrivers({ orgId, search, enabled = false } = {}) {
    return useQuery({
        queryKey: vehicleKeys.drivers(orgId, search),
        queryFn: () => apiHelper.getResource(`${BASE}/drivers/`, {
            orgId,
            params: search ? { search } : {},
        }),
        enabled: !!orgId && enabled,
        select: toList,
        retry: false,
    });
}

// Same-org search via /pdp/360/lookup/employees/ — /emp/cross-org-employees/
// is HR-Admin only. Stopgap; replace once the backend has a real endpoint.
export function useVehicleEmployeeSearch({ orgId, search, excludeIds = [], enabled = false } = {}) {
    return useQuery({
        queryKey: ['vehicle-employee-search', { orgId, search }],
        queryFn: () => {
            const params = new URLSearchParams({ org_id: orgId });
            if (search) params.append('search', search);
            if (excludeIds.length) params.append('exclude_ids', excludeIds.join(','));
            return apiHelper.getResource(`/pdp/360/lookup/employees/?${params.toString()}`);
        },
        enabled: !!orgId && enabled && (search || '').length >= 2,
        select: (data) => {
            const list = data?.results || (Array.isArray(data) ? data : []);
            return list.map(p => ({
                id: p.id,
                name: p.full_name || p.name || `${p.first_name || ''} ${p.last_name || ''}`.trim(),
                jobTitle: p.job_title || '',
            }));
        },
    });
}

// ─── CRUD ────────────────────────────────────────────────────────────────────

export function useCreateVehicleRequest() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ data, orgId }) => apiHelper.postResource(`${BASE}/`, data, { orgId, suppressToast: true }),
        onSuccess: (res, { orgId }) => {
            if (res?.id) queryClient.setQueryData(vehicleKeys.detail(res.id), res);
            queryClient.invalidateQueries({ queryKey: vehicleKeys.lists(orgId) });
            queryClient.invalidateQueries({ queryKey: vehicleKeys.summary(orgId) });
        },
    });
}

export function useUpdateVehicleRequest() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data, orgId }) => apiHelper.patchResource(`${BASE}/${id}/`, data, { orgId, suppressToast: true }),
        onSuccess: (res, { id, orgId }) => {
            if (res?.id) queryClient.setQueryData(vehicleKeys.detail(id), res);
            queryClient.invalidateQueries({ queryKey: vehicleKeys.lists(orgId) });
        },
    });
}

export function useDeleteVehicleRequest() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, orgId }) => apiHelper.deleteResource(`${BASE}/${id}/`, { orgId, suppressToast: true }),
        onSuccess: (_, { id, orgId }) => {
            queryClient.removeQueries({ queryKey: vehicleKeys.detail(id) });
            queryClient.invalidateQueries({ queryKey: vehicleKeys.lists(orgId) });
            queryClient.invalidateQueries({ queryKey: vehicleKeys.summary(orgId) });
        },
    });
}

// ─── WORKFLOW ────────────────────────────────────────────────────────────────

// Response is the full detail payload — write straight to cache, no refetch.
function useVehicleTransition(action) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data = {}, orgId }) => apiHelper.postResource(
            `${BASE}/${id}/${action}/`,
            buildTransitionPayload(data),
            { orgId, suppressToast: true },
        ),
        onSuccess: (res, { id, orgId }) => {
            if (res?.id) queryClient.setQueryData(vehicleKeys.detail(id), res);
            queryClient.invalidateQueries({ queryKey: vehicleKeys.lists(orgId) });
            queryClient.invalidateQueries({ queryKey: vehicleKeys.summary(orgId) });
        },
    });
}

// Requester names the reviewer: { reviewer_id, comment?, signature? }
export const useSubmitVehicleRequest = () => useVehicleTransition('submit');

// One call: { action: 'approve', approver_id, ... } or { action: 'reject', reason }
export const useReviewVehicleRequest = () => useVehicleTransition('review');

// { comment?, signature? }
export const useApproveVehicleRequest = () => useVehicleTransition('approve');

// { reason } — valid from pending_review or pending_approval
export const useRejectVehicleRequest = () => useVehicleTransition('reject');

// { driver_employee_id? | driver_name, driver_phone?, vehicle_reg_no, ... }
// 200 may include a warnings array for overlapping driver bookings.
export const useAssignVehicle = () => useVehicleTransition('assign');

// Note the underscore — the endpoint is dispatch_trip, not dispatch.
export const useDispatchVehicleTrip = () => useVehicleTransition('dispatch_trip');

export const useCompleteVehicleTrip = () => useVehicleTransition('complete');

export const useCancelVehicleRequest = () => useVehicleTransition('cancel');

// ─── PRINTED FORM ────────────────────────────────────────────────────────────

// May return PDF or HTML — opened as a blob tab since the URL needs auth headers.
export function useOpenVehicleRequestPdf() {
    return useMutation({
        mutationFn: async ({ id, orgId }) => {
            // Opened synchronously so the popup blocker allows it.
            const tab = window.open('', '_blank');
            try {
                const blob = await apiHelper.getResource(`${BASE}/${id}/pdf/`, {
                    orgId,
                    responseType: 'blob',
                    suppressToast: true,
                });
                const objectUrl = URL.createObjectURL(blob);
                if (tab) tab.location = objectUrl;
                else window.open(objectUrl, '_blank');
                setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
            } catch (error) {
                tab?.close();
                throw error;
            }
        },
    });
}
